export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/**
 * Resolves a value the API returned (a logo's `/uploads/...` path, say) to a
 * URL that actually loads it.
 *
 * The app and the API are different origins in production (app.reviewcounter.ca
 * vs api.reviewcounter.ca) but the same origin in local dev (CRA's proxy makes
 * `/uploads/...` reach :4000 without anyone noticing). A relative path an
 * `<img>` renders as-is resolves against the PAGE's origin, so this worked
 * locally and 404'd in production. Passes an already-absolute or blob/data URL
 * through untouched.
 */
export function apiAssetUrl(path?: string | null): string | undefined {
  if (!path) return undefined;
  const v = path.trim();
  if (/^(https?:|blob:|data:)/i.test(v)) return v;
  return v.startsWith('/') && !v.startsWith('//') ? `${API_URL}${v}` : undefined;
}

// Set on sign-in and cleared on sign-out. NOT a credential: the real session
// is an httpOnly cookie this code cannot read. This only answers "should the UI
// render as signed in", so the worst a tampered value does is show a logged-out
// user a shell that immediately 401s.
const AUTH_FLAG = 'rc_authed';

export const isSignedIn = () => localStorage.getItem(AUTH_FLAG) === '1';
export const markSignedIn = () => localStorage.setItem(AUTH_FLAG, '1');
export const clearAuthState = () => {
  localStorage.removeItem(AUTH_FLAG);
  localStorage.removeItem('user');
  localStorage.removeItem('rc_currentRestaurantId');
};

/**
 * Every call to the API goes through here.
 *
 * `credentials: 'include'` is what sends the httpOnly session cookie on a
 * cross-origin (but same-site) request to api.reviewcounter.ca. The
 * X-Requested-With header is the CSRF second lock the backend checks on any
 * non-GET: a cross-origin page cannot set a custom header without a preflight,
 * and CORS refuses that preflight.
 */
export async function apiFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
    'X-Requested-With': 'ReviewCounter',
  };
  // Let the browser set the multipart boundary itself for FormData uploads;
  // forcing application/json here would corrupt them.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  // 402 = the trial lapsed with no subscription. Send them somewhere they can
  // actually fix it, rather than letting each page render its own confusing
  // error. Billing calls are exempt or this would loop.
  if (res.status === 402 && !path.startsWith('/api/billing')) {
    if (!window.location.pathname.startsWith('/billing')) {
      window.location.href = '/billing?expired=1';
    }
    throw new Error('SUBSCRIPTION_REQUIRED');
  }

  if (res.status === 401) {
    // The session is gone. Redirect and throw rather than returning undefined:
    // callers can't do anything useful with a dead session, and returning a
    // maybe-undefined Response forced a guard at every one of ~40 call sites
    // (several of which quietly lacked one). The page is navigating away, so
    // whatever catch block this lands in is cosmetic.
    clearAuthState();
    // Setting href to the page we're already on still forces a full reload,
    // which remounts RestaurantProvider, refires its billing fetch, 401s
    // again, and reloads again. An anonymous visitor to /login hit exactly
    // that: an infinite reload loop hammering the API (2026-09-14 incident).
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    throw new Error('SESSION_EXPIRED');
  }
  return res;
}

/** Signs out server-side (the cookie is httpOnly, so only the API can clear it). */
export async function logout() {
  // A logged-out phone must stop showing this account's reviews on its lock
  // screen. Needs the session, so it goes before the logout call.
  try {
    const { disablePush } = await import('./pushClient');
    await disablePush();
  } catch {
    // Never block logging out.
  }
  try {
    await fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'ReviewCounter' },
      credentials: 'include',
    });
  } catch {
    // Network failure shouldn't strand the user in a signed-in-looking UI.
  }
  clearAuthState();
  window.location.href = '/login';
}
